import React, { useRef, useState } from "react";
import { Sparkles, Loader2, UploadCloud, BrainCircuit } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const FOLDERS = [
  "[01] Manuais e Políticas de SSMA", "[02] PGR e PCMSO Vigentes", "[03] DDSs Arquivados",
  "[04] Fichas de EPI", "[05] Laudos Técnicos (Ruído, Calor, LTCAT)",
  "[06] Auditorias ISO 14001 / 45001", "[07] FISPQ / FDS", "[08] Permissões de Trabalho / APR",
];
const DOC_TYPES = ["Manual", "Política", "Procedimento", "Laudo", "Certificado", "Ficha", "FISPQ", "Licença", "Documento"];
const NORMS = ["ISO 45001", "ISO 14001", "ISO 9001", "NR-01", "NR-06", "NR-12", "NR-13", "Geral"];
const STATUS = ["Válido", "Em Revisão", "Vencido"];

export function AiImport({ mode = "document", onDone, testid = "ai-import" }) {
  const isEmp = mode === "employees";
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null); // suggestion object for documents
  const inputRef = useRef(null);

  const reset = () => { setReview(null); setBusy(false); };

  const handle = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "png", "jpg", "jpeg", "webp"].includes(ext)) {
      toast.error("Use PDF, PNG ou JPG para leitura por IA");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (isEmp) {
        const { data } = await api.post("/employees/import-pdf", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
        toast.success(`${data.imported} funcionário(s) cadastrado(s) pela IA`);
        setOpen(false);
        onDone && onDone();
      } else {
        const { data } = await api.post("/documents/analyze", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
        toast.success("IA leu o documento — confira os campos antes de salvar");
        setReview(data.suggestion);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha na leitura por IA");
    } finally {
      setBusy(false);
    }
  };

  const setR = (k, v) => setReview((p) => ({ ...p, [k]: v }));

  const confirmSave = async () => {
    setBusy(true);
    try {
      await api.post("/documents", {
        title: review.title, folder: review.folder, doc_type: review.doc_type, norm: review.norm,
        status: review.status, valid_until: review.valid_until || null, year: review.year,
        file_id: review.file_id, file_name: review.file_name,
        ai_number: review.ai_number, ai_issue_date: review.ai_issue_date,
      });
      toast.success("Documento salvo no GED");
      setOpen(false); reset();
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => { reset(); setOpen(true); }}
        className="border-emerald-300 bg-emerald-50/50 text-emerald-700 hover:bg-emerald-100" data-testid={`${testid}-btn`}>
        <Sparkles className="mr-1.5 h-4 w-4" /> {isEmp ? "Importar PDF (IA)" : "Analisar com IA"}
      </Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[560px]" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0B2545]">
              <BrainCircuit className="h-5 w-5 text-emerald-600" /> {isEmp ? "Importar funcionários por IA" : review ? "Revisar dados extraídos" : "Ler documento com IA"}
            </DialogTitle>
          </DialogHeader>

          {!review ? (
            <>
              <p className="text-sm text-slate-500">
                {isEmp
                  ? "Envie um PDF/imagem com a lista de funcionários. A IA lê nome, matrícula, cargo e setor e cadastra todos — você pode corrigir depois."
                  : "Envie a Licença (CETESB/AVCB/CADRI), laudo ou certificado. A IA identifica título, tipo, norma e validade; você confere antes de salvar."}
              </p>
              <div
                onClick={() => !busy && inputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handle(e.dataTransfer.files?.[0]); }}
                className="mt-2 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-emerald-200 bg-emerald-50/40 p-8 text-center hover:border-emerald-400"
                data-testid={`${testid}-dropzone`}
              >
                {busy ? <Loader2 className="h-7 w-7 animate-spin text-emerald-600" /> : <UploadCloud className="h-7 w-7 text-emerald-600" />}
                <p className="text-sm font-medium text-slate-700">{busy ? "A IA está lendo o documento..." : "Arraste ou clique para enviar (PDF/imagem)"}</p>
                <p className="text-xs text-slate-400">Powered by Gemini · leitura automática</p>
                <input ref={inputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => handle(e.target.files?.[0])} />
              </div>
            </>
          ) : (
            <div className="grid grid-cols-1 gap-3 py-1 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label className="mb-1 block text-xs font-semibold text-slate-600">Título</Label>
                <Input value={review.title || ""} onChange={(e) => setR("title", e.target.value)} data-testid={`${testid}-title`} />
              </div>
              <div className="sm:col-span-2">
                <Label className="mb-1 block text-xs font-semibold text-slate-600">Pasta</Label>
                <Select value={review.folder} onValueChange={(v) => setR("folder", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{FOLDERS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select>
              </div>
              <div>
                <Label className="mb-1 block text-xs font-semibold text-slate-600">Tipo</Label>
                <Select value={review.doc_type} onValueChange={(v) => setR("doc_type", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{DOC_TYPES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select>
              </div>
              <div>
                <Label className="mb-1 block text-xs font-semibold text-slate-600">Norma</Label>
                <Select value={review.norm} onValueChange={(v) => setR("norm", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{NORMS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select>
              </div>
              <div>
                <Label className="mb-1 block text-xs font-semibold text-slate-600">Validade</Label>
                <Input type="date" value={review.valid_until || ""} onChange={(e) => setR("valid_until", e.target.value)} data-testid={`${testid}-valid`} />
              </div>
              <div>
                <Label className="mb-1 block text-xs font-semibold text-slate-600">Status</Label>
                <Select value={review.status} onValueChange={(v) => setR("status", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STATUS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select>
              </div>
              {review.ai_number ? <p className="sm:col-span-2 text-xs text-slate-400">Nº detectado: <b>{review.ai_number}</b>{review.ai_issue_date ? ` · Emissão: ${review.ai_issue_date}` : ""}</p> : null}
            </div>
          )}

          {review && (
            <DialogFooter>
              <Button variant="outline" onClick={reset}>Reenviar</Button>
              <Button onClick={confirmSave} disabled={busy} className="bg-[#0B2545] hover:bg-[#134074]" data-testid={`${testid}-save`}>
                {busy ? "Salvando..." : "Salvar no GED"}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
